'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { Menu, X, ArrowRight } from 'lucide-react';
import { LANDING_CONTENT } from '../constants/content';
import { createSPAClient } from '@/lib/supabase/client';
import LanguageToggle from './LanguageToggle';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import { PAPER_BAND, PAPER_INK, PAPER_RULE, PAPER_FOCUS, PAPER_VARIANT } from '../ui/paper-tokens';
import { DARK_COMPOUND, DARK_INK, DARK_VARIANT } from '../ui/dark-tokens';
import { LANDING_PRIMARY_CTA_RECIPE } from '../ui/cta-recipe';

// R7 hallmark paper flip: every focus-visible ring in this file shares the
// SAME ring + ring-offset pair (page-base paper is the one background this
// nav ever sits on, transparent-at-top or solid-on-scroll — both resolve to
// PAPER_BAND.base) — composed once here rather than repeated at 6 call sites.
const NAV_FOCUS_RING_PAPER = `focus-visible:outline-none focus-visible:ring-2 ${PAPER_FOCUS.ring} focus-visible:ring-offset-2 ${PAPER_VARIANT.baseRingOffset}`;
// 2026-07-17 R7 integration seam (MUST-FIX a): this navbar is SHARED with 3
// pre-R7 public pages that never joined the paper flip — `ChangelogPage`,
// `BlogPostLayout`, `BlogIndexPage` (all still `bg-[#050505] text-white`
// page chrome). An unconditional paper re-ink here would have broken all
// three visually. Additive `surface?: 'paper' | 'dark'` prop (mirrors
// `StepperRail`'s own R7 pattern), default `'paper'` (byte-identical for the
// real landing page, the only caller that omitted a prop pre-R7 and wants
// the NEW look). The dark branch below reproduces the navbar's EXACT pre-R7
// classes, recovered verbatim from `git diff LandingNavbar.tsx` against the
// R6 tree — frost background, `DARK_INK`/`DARK_VARIANT` ink+ring recipe,
// `text-white` logo, `after:bg-white/60` underline — so the 3 dark callers
// render pixel-identical to before R7, not just "still dark".
const NAV_FOCUS_RING_DARK = `focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FBBF24] focus-visible:ring-offset-2 ${DARK_VARIANT.poleRingOffset}`;

function LandingNavbarInner({ surface = 'paper' }: { surface?: 'paper' | 'dark' }) {
  const paper = surface === 'paper';
  const navFocusRing = paper ? NAV_FOCUS_RING_PAPER : NAV_FOCUS_RING_DARK;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Default FALSE — cold visitors (99% of landing traffic) see the unauthenticated trial
  // CTA immediately from the first render. We only flip to Dashboard AFTER getSession()
  // confirms a live session. This prevents the reverse flash (Dashboard → trial CTA)
  // that would occur if we defaulted to true then fell back. Never default true here.
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Scroll-show/hide: navbar hides on downward scroll past 80px and reappears when the
  // user scrolls back up or reaches the top. Compositor-only: opacity + translateY only,
  // explicit transition-[transform,opacity] (never 'transition:all'). Starts visible
  // (true) so the above-fold CTA is accessible on first paint. Reduced-motion: the
  // transform/opacity state changes but the CSS duration is 200ms and globally settles
  // correctly — no additional reduced-motion block needed for a scroll-state toggle.
  const [isNavVisible, setIsNavVisible] = useState(true);
  const { nav } = LANDING_CONTENT;
  const { t } = useLocale();

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;
    // rAF-throttle: coalesce the burst of scroll events into ONE state update per frame.
    // The raw handler read window.scrollY + called setState on every event — needless
    // main-thread work while scrolling; requestAnimationFrame caps it to the frame rate.
    const update = () => {
      const currentScrollY = window.scrollY;
      setIsScrolled(currentScrollY > 20);
      // Show: scrolling up OR within 80px of the top. Hide: scrolling down past 80px.
      if (currentScrollY < 80 || currentScrollY < lastScrollY) {
        setIsNavVisible(true);
      } else {
        setIsNavVisible(false);
      }
      lastScrollY = currentScrollY;
      ticking = false;
    };
    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    createSPAClient()
      .auth.getSession()
      .then(({ data: { session } }) => {
        // Only swap to Dashboard branch if a real session exists; cold visitors stay on
        // the unauthenticated branch forever (session === null → no state change needed).
        setIsAuthenticated(!!session);
      });
  }, []);

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50',
        // Background transition (background-color + border-color only — never 'transition:all')
        'transition-[background-color,border-color,transform,opacity] duration-200',
        // R7 hallmark paper flip: editorial genre bans glassmorphism — the
        // scrolled/menu-open state is now a SOLID paper band + hairline rule,
        // no blur/saturate/shadow (design contract L1.1). Top-of-page state
        // stays transparent, showing the paper hero band through.
        isScrolled || isMobileMenuOpen
          ? paper
            ? `${PAPER_BAND.base.bg} border-b ${PAPER_RULE.hairline.border}`
            : `${DARK_COMPOUND.navFrostBg} border-b border-white/10 backdrop-blur-xl backdrop-saturate-150`
          : 'bg-transparent',
        // Scroll show/hide: translate up + fade out when hidden; snap back when visible.
        // translateY(-100%) moves only the navbar height — compositor-only (no layout shift).
        // Reduced-motion: the state still changes, so keyboard users always reach
        // the navbar by scrolling up; screen-readers are unaffected.
        // R2-2 (2026-07-10 review round): reduced motion keeps the opacity fade but
        // drops the translate + shortens the duration — never a dead or instant snap.
        'motion-reduce:transform-none motion-reduce:duration-150',
        !isNavVisible && '-translate-y-full opacity-0 pointer-events-none'
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between md:grid md:grid-cols-[auto_minmax(0,1fr)_auto] h-16 md:h-20">
          {/* Logo */}
          <Link
            href="/"
            prefetch={false}
            className={cn(
              'flex items-center gap-2 font-bold text-lg tracking-tight rounded-sm',
              paper ? PAPER_INK.primary.text : 'text-white',
              navFocusRing
            )}
          >
            {/* Real logo mark (C0): public/logo-nobg.png replaces the hand-built gradient
                "C" tile. priority — above-the-fold, first paint on every landing visit. */}
            <Image
              src="/logo-nobg.png"
              alt="CMG Workflow"
              width={32}
              height={32}
              priority
              className="w-8 h-8 object-contain"
            />
            <span>{nav.logo}</span>
          </Link>

          {/* Desktop Navigation -- left-anchored after the wordmark (R6 judge
              §2.4: must NOT read as optically centered between unequal outer
              groups); justify-self-start pins it to the grid cell's start
              edge, ml-10/xl:ml-14 sets the gap after the logo. */}
          <div className="hidden md:flex items-center gap-6 justify-self-start ml-10 xl:ml-14">
            {nav.links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={cn(
                  'relative text-sm transition-colors rounded-sm',
                  paper
                    ? cn(PAPER_INK.secondary.text, PAPER_VARIANT.primaryHoverText)
                    : cn(DARK_INK.secondary.text, 'hover:text-white'),
                  navFocusRing,
                  // ::after underline scaleX hover — compositor-only (transform), no entrance.
                  // overflow-hidden on the anchor itself would clip the underline; use
                  // relative + after:absolute positioning instead. after:bottom-[-2px]
                  // places the line just below the text baseline. R7: ink underline via
                  // the black/opacity alpha-overlay technique (gate-legal, no bracket
                  // hex) — paper-tokens has no bg- form of PAPER_INK to reuse here.
                  'after:absolute after:bottom-[-2px] after:left-0 after:h-px after:w-full',
                  paper ? 'after:bg-black/60' : 'after:bg-white/60',
                  'after:scale-x-0 after:origin-left',
                  'after:transition-transform after:duration-200',
                  'hover:after:scale-x-100'
                )}
              >
                {t(link.label)}
              </a>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageToggle surface={surface} />
            {isAuthenticated ? (
              <Link
                href="/dashboard"
                prefetch={false}
                className={cn(
                  LANDING_PRIMARY_CTA_RECIPE,
                  // R2-2 (2026-07-10 review round) -> R6: recipe now sourced from cta-recipe.ts.
                  'group inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm'
                )}
              >
                {t({ en: 'Go to Dashboard', ro: 'Mergi la Dashboard' })}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ) : (
              <>
                <Link
                  href="/auth/login"
                  prefetch={false}
                  className={cn(
                    'text-sm transition-colors px-4 py-2 rounded-sm',
                    navFocusRing,
                    paper
                      ? cn(PAPER_VARIANT.primaryHoverText, PAPER_INK.muted.text)
                      : cn('hover:text-white', DARK_INK.tertiary.text)
                  )}
                >
                  {t(nav.account)}
                </Link>
                {/* prefetch intentionally kept (default true) — /auth/register is the single high-conversion destination;
                    both desktop and mobile register CTAs keep prefetch for consistency. */}
                <Link
                  href="/auth/register"
                  // v2 bio-link engagement pipeline (contract 3/6): desktop +
                  // mobile register CTAs share the one canonical event_name
                  // "cta_navbar".
                  data-track="cta_navbar"
                  className={cn(
                    LANDING_PRIMARY_CTA_RECIPE,
                    // R2-2 (2026-07-10 review round) -> R6: recipe now sourced from cta-recipe.ts.
                    'group inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm'
                  )}
                >
                  {t(nav.cta.primary)}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={cn(
              'md:hidden p-2 rounded-sm',
              navFocusRing,
              paper
                ? cn(PAPER_VARIANT.primaryHoverText, PAPER_INK.muted.text)
                : cn('hover:text-white', DARK_INK.tertiary.text)
            )}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className={cn(
              'md:hidden py-4 border-t',
              paper ? PAPER_RULE.hairline.border : 'border-white/5'
            )}
          >
            <div className="flex flex-col gap-2">
              {nav.links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    'py-2 px-4 rounded-lg transition-colors',
                    navFocusRing,
                    paper
                      ? cn(
                          'hover:bg-black/5',
                          PAPER_VARIANT.primaryHoverText,
                          PAPER_INK.secondary.text
                        )
                      : cn('hover:text-white hover:bg-white/5', DARK_INK.secondary.text)
                  )}
                >
                  {t(link.label)}
                </a>
              ))}
              <hr className={cn('my-2', paper ? PAPER_RULE.hairline.border : 'border-white/5')} />
              <div className="px-4 py-2">
                <LanguageToggle surface={surface} />
              </div>
              <hr className={cn('my-2', paper ? PAPER_RULE.hairline.border : 'border-white/5')} />
              {isAuthenticated ? (
                <Link
                  href="/dashboard"
                  prefetch={false}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={cn(
                    LANDING_PRIMARY_CTA_RECIPE,
                    // R2-2 (2026-07-10 review round) -> R6: recipe now sourced from cta-recipe.ts.
                    'flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold'
                  )}
                >
                  {t({ en: 'Go to Dashboard', ro: 'Mergi la Dashboard' })}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              ) : (
                <>
                  <Link
                    href="/auth/login"
                    prefetch={false}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={cn(
                      'py-2 px-4 rounded-sm',
                      navFocusRing,
                      paper
                        ? cn(PAPER_VARIANT.primaryHoverText, PAPER_INK.muted.text)
                        : cn('hover:text-white', DARK_INK.tertiary.text)
                    )}
                  >
                    {t(nav.account)}
                  </Link>
                  <Link
                    href="/auth/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    // v2 bio-link engagement pipeline (contract 3/6): shares
                    // "cta_navbar" with the desktop register CTA above.
                    data-track="cta_navbar"
                    className={cn(
                      LANDING_PRIMARY_CTA_RECIPE,
                      // R2-2 (2026-07-10 review round) -> R6: recipe now sourced from cta-recipe.ts.
                      'flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-semibold'
                    )}
                  >
                    {t(nav.cta.primary)}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

export default function LandingNavbar({ surface }: { surface?: 'paper' | 'dark' }) {
  return <LandingNavbarInner surface={surface} />;
}
